import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { DateTime } from "luxon";
import { fetchApi } from "@/lib/api";
import type { CareReviewSortColumn } from "@/lib/care-reviews/review-list";
import type { Escalation } from "./escalations";
import type { RiskFlag } from "./risk-flagging";

export type AssessmentType = "phq9" | "gad7" | "who5";
export const ASSESSMENT_OF_SYMPTOMS = [
  "improved",
  "escalated",
  "maintained",
  "initial",
] as const;
export type AssessmentOfSymptoms = (typeof ASSESSMENT_OF_SYMPTOMS)[number];
export type CareSnapshotWindow = "7d" | "90d" | "all";
export type CareHistoryFilter =
  | "all"
  | "assessments"
  | "presentation"
  | "care_updates"
  | "flags"
  | "escalations";
export type CareHistoryType =
  | "assessment"
  | "presentation"
  | "care_update"
  | "escalation"
  | "flag"
  | "account_update";

export interface CareUser {
  id: string;
  role: string;
  name: string | null;
  profile_picture_url: string | null;
}

export interface CarePresentation {
  code: string;
  description: string;
}

export interface CareUpdateFields {
  assessment_of_symptoms: AssessmentOfSymptoms;
  clinical_summary: string;
  diagnostic_support_statement: string;
}

export interface CareUpdate {
  assessment_of_symptoms: string | null;
  clinical_summary: string | null;
  diagnostic_support_statement: string | null;
  author: CareUser | null;
  submitted_at: string | null;
}

export interface CareOpenReview {
  id: string;
  status: "pending" | "draft";
  review_type: "intake" | "day_30" | "quarterly";
  recommended: CarePresentation;
  rationale: string | null;
  presentation: CarePresentation | null;
  assessment_of_symptoms: string | null;
  clinical_summary: string | null;
  diagnostic_support_statement: string | null;
  /** Survey responses that triggered this review, oldest first. */
  assessment_ids: string[];
  created_at: string;
  due_at: string | null;
  overdue: boolean;
}

export interface CarePage {
  mentor: CareUser | null;
  days_on_platform: number;
  assessment: AssessmentType;
  latest_score: { score: number; severity: string; taken_at: string } | null;
  care_reviews_enabled: boolean;
  presentation: CarePresentation | null;
  care_update: CareUpdate | null;
  open_review: CareOpenReview | null;
  next_review: {
    state: "none" | "today" | "days" | "awaiting_survey" | "paused";
    days: number | null;
  };
  clinical_advisor: { name: string; email: string };
}

export interface CareHistoryItem {
  id: string;
  type: CareHistoryType;
  occurred_at: string;
  title: string;
  subtitle: string | null;
  actor: CareUser | null;
  tag: string;
  expandable: boolean;
}

interface CareHistoryPage {
  items: CareHistoryItem[];
  has_more: boolean;
}

interface CareUpdatePayload {
  assessment_of_symptoms: string | null;
  clinical_summary: string | null;
  diagnostic_support_statement: string | null;
}

export type CareHistoryDetail =
  | {
      type: "assessment";
      payload: {
        assessment: AssessmentType;
        score: number;
        max_score: number;
        severity: string;
        taken_at: string;
        answers: { question: string; answer: string }[];
      };
    }
  | {
      type: "presentation";
      payload: CareUpdatePayload & {
        event: "assigned" | "changed" | "reviewed";
        code: string;
        description: string;
      };
    }
  | { type: "care_update"; payload: CareUpdatePayload }
  | { type: "escalation"; payload: Escalation }
  | { type: "flag"; payload: { alerts: RiskFlag[] } };

export interface CareTagFrequency {
  name: string;
  days: number;
}

export interface CareTrajectoryPoint {
  month: string;
  score: number | null;
  severity: string | null;
  escalations: number;
  flags: number;
}

export interface CareSnapshot {
  id: string;
  window: CareSnapshotWindow;
  period_start: string;
  period_end: string;
  engagement_days: number;
  messages_sent: number;
  symptoms: CareTagFrequency[];
  environmental_factors: CareTagFrequency[];
  symptom_trajectory: string | null;
  conversation_summary: string | null;
  ai_status: "pending" | "ready" | "failed";
  trajectory: CareTrajectoryPoint[];
  generated_at: string;
  feedback_helpful: boolean | null;
  feedback_comment: string | null;
}

export interface CareDrafts {
  clinical_summary: string | null;
  diagnostic_support_statement: string | null;
}

const HISTORY_FIRST_PAGE = 10;
const HISTORY_NEXT_PAGE = 15;

export const careKeys = {
  page: (menteeId: string) => ["care", menteeId] as const,
  history: (menteeId: string) => ["care", menteeId, "history"] as const,
  detail: (menteeId: string, type: string, id: string) =>
    ["care", menteeId, "history", type, id] as const,
  snapshot: (menteeId: string, window: CareSnapshotWindow) =>
    ["care", menteeId, "snapshot", window] as const,
  codes: (q: string) => ["care", "diagnostic-codes", q] as const,
  reviews: (query: CareReviewsQuery) =>
    [
      "care",
      "reviews",
      query.status,
      query.month ?? "",
      query.supervisorId ?? "",
      query.sort ?? "",
      query.sort_direction ?? "",
    ] as const,
  reviewCount: ["care", "review-count"] as const,
};

export const useCarePage = (menteeId: string, enabled = true) =>
  useQuery({
    queryKey: careKeys.page(menteeId),
    enabled,
    queryFn: async () => {
      const { data } = await fetchApi<CarePage>({
        url: `/v1/care/${menteeId}`,
        method: "GET",
      });
      return data;
    },
  });

export const useCareHistory = (menteeId: string, filter: CareHistoryFilter) =>
  useInfiniteQuery({
    queryKey: [...careKeys.history(menteeId), filter],
    initialPageParam: { offset: 0, limit: HISTORY_FIRST_PAGE },
    queryFn: async ({ pageParam }) => {
      const { data } = await fetchApi<CareHistoryPage>({
        url: `/v1/care/${menteeId}/history`,
        method: "GET",
        params: { ...pageParam, filter },
      });
      return data;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.has_more
        ? {
            offset: pages.reduce((sum, page) => sum + page.items.length, 0),
            limit: HISTORY_NEXT_PAGE,
          }
        : undefined,
  });

const careHistoryDetailOptions = (
  menteeId: string,
  type: CareHistoryType,
  id: string,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: careKeys.detail(menteeId, type, id),
    queryFn: async () => {
      const { data } = await fetchApi<CareHistoryDetail>({
        url: `/v1/care/${menteeId}/history/${type}/${id}`,
        method: "GET",
      });
      return data;
    },
    enabled,
  });

export const useCareHistoryDetail = (
  menteeId: string,
  type: CareHistoryType,
  id: string,
  enabled: boolean,
) => useQuery(careHistoryDetailOptions(menteeId, type, id, enabled));

export const useCareAssessments = (
  menteeId: string,
  ids: string[],
  enabled: boolean,
) =>
  useQueries({
    queries: ids.map((id) =>
      careHistoryDetailOptions(menteeId, "assessment", id, enabled),
    ),
    combine: (results) => ({
      isPending: results.some((r) => r.isPending),
      isError: results.length > 0 && results.every((r) => r.isError),
      data: results.flatMap((r) =>
        r.data?.type === "assessment" ? [r.data.payload] : [],
      ),
    }),
  });

export const useCareSnapshot = (
  menteeId: string,
  window: CareSnapshotWindow,
  poll = true,
) =>
  useQuery({
    queryKey: careKeys.snapshot(menteeId, window),
    queryFn: async () => {
      const { data } = await fetchApi<CareSnapshot>({
        url: `/v1/care/${menteeId}/snapshot`,
        method: "GET",
        params: { window },
      });
      return data;
    },
    refetchInterval: (query) =>
      poll && query.state.data?.ai_status === "pending" ? 3000 : false,
  });

export const useDiagnosticCodes = (q: string) =>
  useInfiniteQuery({
    queryKey: careKeys.codes(q),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await fetchApi<{
        codes: CarePresentation[];
        has_more: boolean;
      }>({
        url: "/v1/care/diagnostic-codes",
        method: "GET",
        params: { q: q || undefined, offset: pageParam, limit: 25 },
      });
      return data;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.has_more
        ? pages.reduce((sum, page) => sum + page.codes.length, 0)
        : undefined,
  });

export const useGenerateCareDrafts = (menteeId: string) =>
  useMutation({
    mutationFn: async (body: { presentation?: CarePresentation }) => {
      const { data } = await fetchApi<CareDrafts>({
        url: `/v1/care/${menteeId}/drafts`,
        method: "POST",
        data: body,
      });
      return data;
    },
  });

export const useSnapshotFeedback = (menteeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { helpful: boolean; comment?: string }) => {
      await fetchApi({
        url: `/v1/care/${menteeId}/snapshot/feedback`,
        method: "POST",
        data: body,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [...careKeys.page(menteeId), "snapshot"],
      }),
  });
};

function useCarePageMutation<TBody>(
  menteeId: string,
  method: "POST" | "PUT",
  path: string,
  refreshReviews = false,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TBody) => {
      const { data } = await fetchApi<CarePage>({
        url: `/v1/care/${menteeId}/${path}`,
        method,
        data: body,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(careKeys.page(menteeId), data);
      queryClient.invalidateQueries({ queryKey: careKeys.history(menteeId) });
      if (!refreshReviews) return;
      queryClient.invalidateQueries({ queryKey: ["care", "reviews"] });
      queryClient.invalidateQueries({ queryKey: careKeys.reviewCount });
    },
  });
}

export type SaveReviewDraftBody = {
  assessment_of_symptoms?: AssessmentOfSymptoms | null;
  clinical_summary?: string | null;
  diagnostic_support_statement?: string | null;
  presentation?: CarePresentation | null;
};

export const useSaveReviewDraft = (menteeId: string) =>
  useCarePageMutation<SaveReviewDraftBody>(menteeId, "PUT", "review/draft");

export const useSubmitReview = (menteeId: string) =>
  useCarePageMutation<CareUpdateFields & { presentation?: CarePresentation }>(
    menteeId,
    "POST",
    "review/submit",
    true,
  );

export const useWriteCareUpdate = (menteeId: string) =>
  useCarePageMutation<CareUpdateFields>(menteeId, "POST", "updates");

export const useChangePresentation = (menteeId: string) =>
  useCarePageMutation<CareUpdateFields & { presentation: CarePresentation }>(
    menteeId,
    "POST",
    "presentation",
  );

export interface CareReviewTodo {
  id: string;
  review_type: "intake" | "day_30" | "quarterly";
  status: "pending" | "draft" | "submitted";
  created_at: string;
  due_at: string | null;
  submitted_at: string | null;
  overdue: boolean;
  seen_at: string | null;
  recommended: CarePresentation | null;
  current: CarePresentation | null;
  code: string | null;
  presentation_event: "assigned" | "changed" | "reviewed" | null;
  supervisors: { id: string; name: string | null }[] | null;
  mentee: CareUser;
}

export type CareReviewListStatus = "open" | "closed";

export type CareReviewCounts = {
  open: number;
  closed: number;
  visible: boolean;
};

type CareReviewsQuery = {
  status: CareReviewListStatus;
  month?: string;
  supervisorId?: string;
  sort?: CareReviewSortColumn;
  sort_direction?: "asc" | "desc";
};

export const useCareReviews = (query: CareReviewsQuery, enabled = true) =>
  useQuery({
    queryKey: careKeys.reviews(query),
    enabled,
    placeholderData: (previousData, previousQuery) => {
      const [, , status, month, supervisorId] = careKeys.reviews(query);
      const [, , previousStatus, previousMonth, previousSupervisorId] =
        previousQuery?.queryKey ?? [];
      if (
        previousStatus === status &&
        previousMonth === month &&
        previousSupervisorId === supervisorId
      ) {
        return previousData;
      }
      return undefined;
    },
    queryFn: async () => {
      const { data } = await fetchApi<{ reviews: CareReviewTodo[] }>({
        url: "/v1/care/reviews",
        params: {
          status: query.status,
          ...(query.month ? { month: query.month } : {}),
          ...(query.supervisorId ? { supervisor_id: query.supervisorId } : {}),
          ...(query.sort
            ? {
                sort: query.sort,
                ...(query.sort_direction
                  ? { sort_direction: query.sort_direction }
                  : {}),
              }
            : {}),
        },
      });
      return data.reviews;
    },
  });

const EMPTY_REVIEW_COUNTS: CareReviewCounts = {
  open: 0,
  closed: 0,
  visible: false,
};

export const useCareReviewCount = (enabled: boolean) => {
  const query = useQuery({
    queryKey: careKeys.reviewCount,
    enabled,
    queryFn: async () => {
      const { data } = await fetchApi<CareReviewCounts>({
        url: "/v1/care/reviews/count",
      });
      return data;
    },
  });
  return {
    ...(enabled && query.data ? query.data : EMPTY_REVIEW_COUNTS),
    isPending: enabled && query.isPending,
    isError: enabled && query.isError,
    refetch: query.refetch,
  };
};

export const useMarkCareReviewSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await fetchApi<{ seen_at: string }>({
        url: `/v1/care/review/${id}/mark-seen`,
        method: "PATCH",
      });
      return data;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["care", "reviews"] });
      const previous = queryClient.getQueriesData<CareReviewTodo[]>({
        queryKey: ["care", "reviews"],
      });
      patchReviewSeen(queryClient, id, DateTime.utc().toISO());
      return { previous };
    },
    onError: (_error, _id, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (data, id) => {
      patchReviewSeen(queryClient, id, data.seen_at);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["care", "reviews"] });
    },
  });
};

function patchReviewSeen(
  queryClient: QueryClient,
  id: string,
  seenAt: string | null,
) {
  queryClient.setQueriesData<CareReviewTodo[]>(
    { queryKey: ["care", "reviews"] },
    (current) =>
      current?.map((review) =>
        review.id === id ? { ...review, seen_at: seenAt } : review,
      ),
  );
}

export const useUndoCareReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (menteeId: string) => {
      const { data } = await fetchApi<CarePage>({
        url: `/v1/care/${menteeId}/review/undo`,
        method: "POST",
      });
      return data;
    },
    onSuccess: (data, menteeId) => {
      queryClient.setQueryData(careKeys.page(menteeId), data);
      queryClient.invalidateQueries({ queryKey: ["care", "reviews"] });
      queryClient.invalidateQueries({ queryKey: careKeys.reviewCount });
    },
  });
};
